import { FormattedMessage } from 'react-intl';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { getDiscountPercentage } from '@alltrails/payment/utils/plan';
import type Coupon from '@alltrails/types/Coupon';
import type Plans from '@alltrails/types/Plans';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';

type Params = {
  coupon?: Coupon;
  isPeakPromoEligible?: boolean;
  isPlusPromoEligible?: boolean;
  plans: Plans;
  subscriptionUser: SubscriptionUser;
  tierSelected: PurchaseTier;
};

// CTA copy for the plans landing page. Unlike the upsell modals, an applicable coupon is advertised ahead
// of promo eligibility, since the visitor arrived here through the promo.
export default function getPlansLandingPageCtaText({
  coupon,
  isPeakPromoEligible,
  isPlusPromoEligible,
  plans,
  subscriptionUser,
  tierSelected
}: Params) {
  if (coupon?.type === 'FreeTrialCoupon') {
    return <FormattedMessage defaultMessage="Start your free trial" />;
  }

  if (coupon?.type === 'SubscriptionCoupon') {
    if (coupon.subscriptionTier === 'any' && coupon.discountPercentage) {
      return (
        <FormattedMessage
          defaultMessage="Save {percent, number, ::percent} on {tierName}"
          values={{ percent: coupon.discountPercentage / 100, tierName: tierSelected === 'peak' ? 'Peak' : 'Plus' }}
        />
      );
    }

    if (tierSelected === 'plus' && coupon.subscriptionTier === 'plus') {
      return (
        <FormattedMessage
          defaultMessage="Save {percent, number, ::percent} on {tierName}"
          values={{ percent: getDiscountPercentage(plans.plus, coupon) / 100, tierName: 'Plus' }}
        />
      );
    }

    if (tierSelected === 'peak' && coupon.subscriptionTier === 'peak') {
      return (
        <FormattedMessage
          defaultMessage="Save {percent, number, ::percent} on {tierName}"
          values={{ percent: getDiscountPercentage(plans.peak, coupon) / 100, tierName: 'Peak' }}
        />
      );
    }
  }

  if ((tierSelected === 'peak' && isPeakPromoEligible) || (tierSelected === 'plus' && isPlusPromoEligible)) {
    return <FormattedMessage defaultMessage="Start your free trial" />;
  }

  if (tierSelected === 'peak') {
    // Trialing subscribers have no remaining paid time to refund, so they are not treated as upgrades.
    if (subscriptionUser.canUpgradeExistingSubscription && !subscriptionUser.subscription?.trial) {
      return <FormattedMessage defaultMessage="Upgrade to Peak" />;
    }

    return <FormattedMessage defaultMessage="Continue with Peak" />;
  }

  return <FormattedMessage defaultMessage="Continue with Plus" />;
}
